import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import {
  MFAReducer,
  SCREEN_DISPLAY,
  useMFAFlow,
} from "common-client/utils/useMFAFlow";
import {
  AdminLoginDocument,
  GetUserAccountsQuery,
  UserLoginDocument,
} from "../../generated/graphql";
import { LoginLocationState, useRedirectParamParser } from "../../utils/params";
import { track } from "../../utils/tracking";
import FullPageBackgroundWithLogo from "../Common/FullPageBackgroundWithLogo";
import { LoginForm } from "./__styles__/LoginPage";
import { Container } from "./CommonLoginComponents/__styles__/FormWithFullPageBackground";
import { ProvideCredentials } from "./ProvideCredentials";
import { ProvidePhoneCode } from "./ProvidePhoneCode";
import { ProvidePhoneNumber } from "./ProvidePhoneNumber";
import { RequestMFAResetConfirmation } from "./RequestMFAResetConfirmation";
import { SelectRequestUser } from "./SelectRequestUser";
import { ResetFlowCode } from "./Totp/ResetFlowCode";
import { SetupFlowCode } from "./Totp/SetupFlowCode";
import { SetupTotp } from "./Totp/SetupTotp";
import { VerifyCode } from "./Totp/VerifyCode";

export interface LoginPageProps {
  loginMutation: typeof AdminLoginDocument | typeof UserLoginDocument;
  showPasswordReset?: boolean;
  initialTitle: string;
  fallbackPath: string;
  event: string;
  startingEmail?: string;
  accounts: NonNullable<GetUserAccountsQuery["getUserAccountsByEmail"]>;
  currentAccount: Maybe<
    NonNullable<GetUserAccountsQuery["getUserAccountsByEmail"]>[0]
  >;
  useTotp?: boolean;
}

export type LoginFormStructure = {
  email?: Maybe<string>;
  password?: Maybe<string>;
  phoneNumber?: Maybe<string>;
  code?: Maybe<string>;
  requestedUserId?: Maybe<string>;
};

const LoginPage = ({
  loginMutation,
  showPasswordReset,
  initialTitle,
  event,
  startingEmail,
  fallbackPath,
  accounts,
  currentAccount,
}: LoginPageProps) => {
  const location = useLocation<LoginLocationState>();

  const useFormProps = useForm<LoginFormStructure>({
    defaultValues: {
      email: startingEmail,
    },
  });

  const redirectPath = useRedirectParamParser({
    routeName: "provideCredentials",
    routerLocation: location,
    fallbackPath,
  });

  const onLoginSuccess = () => {
    track(event);

    window.location.href = redirectPath;
  };

  const [state, dispatch] = useMFAFlow({
    defaultValue: {
      currentScreen: SCREEN_DISPLAY.PROVIDE_CREDENTIALS,
      error: null,
    },
    useReducer: React.useReducer as MFAReducer,
  });

  const error = state.error ?? null;

  return (
    <FullPageBackgroundWithLogo>
      <Container>
        <FormProvider {...useFormProps}>
          <LoginForm>
            {state.currentScreen === SCREEN_DISPLAY.PROVIDE_CREDENTIALS && (
              <ProvideCredentials
                currentAccount={currentAccount ?? null}
                dispatch={dispatch}
                error={error}
                loginMutation={loginMutation}
                onSuccess={onLoginSuccess}
                showPasswordReset={showPasswordReset}
                title={initialTitle}
                accounts={accounts}
              />
            )}
            {state.currentScreen === SCREEN_DISPLAY.PROVIDE_PHONE && (
              <ProvidePhoneNumber dispatch={dispatch} error={error} />
            )}
            {state.currentScreen === SCREEN_DISPLAY.PROVIDE_CODE && (
              <ProvidePhoneCode
                allowAnotherNumber={!state.phoneNumberVerified!}
                allowResetRequest={state.phoneNumberVerified!}
                dispatch={dispatch}
                error={error}
                maskedPhoneNumber={state.phoneNumber!}
                onSuccess={onLoginSuccess}
              />
            )}
            {state.currentScreen === SCREEN_DISPLAY.REQUEST_RESET && (
              <SelectRequestUser dispatch={dispatch} error={error} />
            )}
            {state.currentScreen === SCREEN_DISPLAY.CONFIRM_RESET && (
              <RequestMFAResetConfirmation />
            )}
            {state.currentScreen === SCREEN_DISPLAY.VERIFY_TOTP_CODE && (
              <VerifyCode
                onLoginSuccess={onLoginSuccess}
                dispatch={dispatch}
                error={error}
              />
            )}
            {state.currentScreen === SCREEN_DISPLAY.RESET_FLOW_TOTP_CODE && (
              <ResetFlowCode dispatch={dispatch} error={error} />
            )}
            {state.currentScreen === SCREEN_DISPLAY.SETUP_FLOW_TOTP_CODE && (
              <SetupFlowCode
                onLoginSuccess={onLoginSuccess}
                dispatch={dispatch}
                qrCode={state.qrCode}
                error={error}
              />
            )}
            {state.currentScreen === SCREEN_DISPLAY.SETUP_TOTP && (
              <SetupTotp
                dispatch={dispatch}
                error={error}
                qrCode={state.qrCode}
              />
            )}
          </LoginForm>
        </FormProvider>
      </Container>
    </FullPageBackgroundWithLogo>
  );
};

export default LoginPage;
